/**
 * Custom LUV Juice JavaScript
 * Handles AJAX Add to Bag, Cart Drawer Open/Close, Quantity Controls,
 * Item Removal, Live Totals, and Header Counter Updates.
 * Prefix-scoped to avoid conflicts with main theme scripts.
 */

(function () {
  'use strict';

  if (window.__customLuvJuiceLoaded) return;
  window.__customLuvJuiceLoaded = true;

  const STORAGE_KEY = '_custom_demo_cart_lines';

  function getDemoCart() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      return [];
    }
  }

  function setDemoCart(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      console.error(e);
    }
  }

  function showToast(message) {
    let toast = document.getElementById('custom-toast-notification');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'custom-toast-notification';
      toast.className = 'custom-toast';
      document.body.appendChild(toast);
    }
    toast.textContent = message || 'Added to bag';
    toast.classList.add('custom-toast--show');

    if (window._customToastTimeout) clearTimeout(window._customToastTimeout);
    window._customToastTimeout = setTimeout(function () {
      toast.classList.remove('custom-toast--show');
    }, 2800);
  }

  function updateCartCounters(count) {
    const badges = document.querySelectorAll('.custom-header__cart-count');
    badges.forEach(function (badge) {
      badge.textContent = count;
    });
    const drawerBadges = document.querySelectorAll('.custom-cart-drawer__count');
    drawerBadges.forEach(function (badge) {
      badge.textContent = count;
    });
  }

  function openCartDrawer() {
    const drawer = document.getElementById('custom-cart-drawer');
    if (!drawer) return;
    drawer.classList.add('custom-cart-drawer--open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('custom-cart-drawer-open');

    const trigger = document.querySelector('[data-custom-cart-open]');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) { syncDrawerPropertiesFromCart(cart); })
      .catch(function () {});
  }

  function closeCartDrawer() {
    const drawer = document.getElementById('custom-cart-drawer');
    if (!drawer) return;
    drawer.classList.remove('custom-cart-drawer--open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('custom-cart-drawer-open');

    const trigger = document.querySelector('[data-custom-cart-open]');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  }

  function openMobileMenu() {
    const drawer = document.getElementById('custom-mobile-menu-drawer');
    if (!drawer) return;
    drawer.classList.add('custom-mobile-menu--open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('custom-mobile-menu-open');
    const trigger = document.querySelector('[data-custom-mobile-menu-open]');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
  }

  function closeMobileMenu() {
    const drawer = document.getElementById('custom-mobile-menu-drawer');
    if (!drawer) return;
    drawer.classList.remove('custom-mobile-menu--open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('custom-mobile-menu-open');
    const trigger = document.querySelector('[data-custom-mobile-menu-open]');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  }

  function handleMobileMenuClicks(e) {
    if (e.target.closest('[data-custom-mobile-menu-open]')) {
      e.preventDefault();
      openMobileMenu();
      return;
    }
    if (e.target.closest('[data-custom-mobile-menu-close]')) {
      closeMobileMenu();
      return;
    }
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  /* Enrich any real Shopify cart items in the drawer with their live line item properties */
  function syncDrawerPropertiesFromCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    const drawer = document.getElementById('custom-cart-drawer');
    if (!drawer) return;

    const domItems = drawer.querySelectorAll('.custom-cart-item:not([data-demo-item="true"])');
    domItems.forEach(function (domItem, idx) {
      const lineKey = domItem.getAttribute('data-line-key');
      const itemData = cart.items.find(function (it) { return it.key === lineKey; }) || cart.items[idx];
      if (!itemData || !itemData.properties) return;

      const details = domItem.querySelector('.custom-cart-item__details');
      if (!details) return;

      let propsContainer = domItem.querySelector('.custom-cart-item__properties');
      if (!propsContainer) {
        propsContainer = document.createElement('div');
        propsContainer.className = 'custom-cart-item__properties';
        const actions = domItem.querySelector('.custom-cart-item__actions');
        if (actions) {
          details.insertBefore(propsContainer, actions);
        } else {
          details.appendChild(propsContainer);
        }
      }

      const pKeys = Object.keys(itemData.properties);
      let propsHtml = '';
      const seen = {};

      pKeys.forEach(function (rawKey) {
        const val = itemData.properties[rawKey];
        if (!val || rawKey.startsWith('_')) return;

        const lowerKey = rawKey.toLowerCase().trim();
        // Do not display Size (it's already in the variant title)
        if (lowerKey === 'size') return;
        if (seen[lowerKey]) return;
        seen[lowerKey] = true;

        const displayKey = rawKey.charAt(0).toUpperCase() + rawKey.slice(1);
        propsHtml += '<div class="custom-cart-item__property"><span class="custom-cart-item__prop-name">' + displayKey + ':</span> <span class="custom-cart-item__prop-val">' + val + '</span></div>';
      });

      if (propsHtml) {
        propsContainer.innerHTML = propsHtml;
        propsContainer.style.display = 'flex';
      }
    });
  }

  function syncDemoCartWithDrawer(realCart) {
    const demoItems = getDemoCart();
    const totalCount = (realCart ? realCart.item_count : 0) + demoItems.reduce(function (sum, it) { return sum + it.quantity; }, 0);
    updateCartCounters(totalCount);

    const drawer = document.getElementById('custom-cart-drawer');
    if (!drawer) return;

    const itemsContainer = drawer.querySelector('[data-custom-cart-items]');
    const emptyState = drawer.querySelector('[data-custom-cart-empty]');
    const footer = drawer.querySelector('[data-custom-cart-footer]');
    const subtotalEl = drawer.querySelector('[data-cart-subtotal]');
    const totalEl = drawer.querySelector('[data-cart-total]');

    if (totalCount === 0) {
      if (itemsContainer) itemsContainer.style.display = 'none';
      if (footer) footer.style.display = 'none';
      if (emptyState) emptyState.style.display = 'flex';
      return;
    }

    if (emptyState) emptyState.style.display = 'none';
    if (itemsContainer) itemsContainer.style.display = 'flex';
    if (footer) footer.style.display = 'flex';

    // Synchronize properties on live Shopify line items
    if (realCart) {
      syncDrawerPropertiesFromCart(realCart);
    }

    // Remove any previously appended demo items in the DOM
    if (itemsContainer) {
      const existingDemos = itemsContainer.querySelectorAll('[data-demo-item="true"]');
      existingDemos.forEach(function (el) { el.remove(); });

      // Append current demo items
      demoItems.forEach(function (demo, idx) {
        const itemEl = document.createElement('div');
        itemEl.className = 'custom-cart-item';
        itemEl.setAttribute('data-demo-item', 'true');
        itemEl.setAttribute('data-demo-index', idx);

        let propsHtml = '';
        if (demo.properties && typeof demo.properties === 'object' && Object.keys(demo.properties).length > 0) {
          propsHtml = '<div class="custom-cart-item__properties">';
          Object.keys(demo.properties).forEach(function (key) {
            if (key.startsWith('_') || !demo.properties[key]) return;
            if ((key === 'Size' || key === 'Milk') && demo.subtitle && demo.subtitle.includes(demo.properties[key])) return;
            propsHtml += '<div class="custom-cart-item__property"><span class="custom-cart-item__prop-name">' + key + ':</span> <span class="custom-cart-item__prop-val">' + demo.properties[key] + '</span></div>';
          });
          propsHtml += '</div>';
        } else if (demo.addons) {
          propsHtml = '<div class="custom-cart-item__properties"><div class="custom-cart-item__property"><span class="custom-cart-item__prop-name">Add-ons:</span> <span class="custom-cart-item__prop-val">' + demo.addons + '</span></div></div>';
        }

        itemEl.innerHTML =
          '<div class="custom-cart-item__image-wrap custom-striped-bg-dense">' +
            (demo.image
              ? '<img src="' + demo.image + '" alt="' + demo.title + '" class="custom-cart-item__img">'
              : '<div class="custom-cart-item__placeholder"><svg class="custom-placeholder-svg" viewBox="0 0 100 100"><rect width="100" height="100" fill="#E9E4D7"/><text x="50" y="50" fill="#8C8778" font-family="monospace" font-size="12" text-anchor="middle" dy=".3em">photo</text></svg></div>') +
          '</div>' +
          '<div class="custom-cart-item__details">' +
            '<span class="custom-cart-item__title">' + demo.title + '</span>' +
            (demo.subtitle ? '<span class="custom-cart-item__meta">' + demo.subtitle + '</span>' : '') +
            propsHtml +
            '<div class="custom-cart-item__actions">' +
              '<div class="custom-cart-qty-control">' +
                '<button type="button" class="custom-cart-qty-btn" data-action="demo-decrease" data-demo-index="' + idx + '" aria-label="Decrease quantity">&minus;</button>' +
                '<span class="custom-cart-qty-val">' + demo.quantity + '</span>' +
                '<button type="button" class="custom-cart-qty-btn" data-action="demo-increase" data-demo-index="' + idx + '" aria-label="Increase quantity">&#43;</button>' +
              '</div>' +
              '<button type="button" class="custom-cart-item__remove" data-action="demo-remove" data-demo-index="' + idx + '">Remove</button>' +
            '</div>' +
          '</div>' +
          '<div class="custom-cart-item__price-col">' +
            '<span class="custom-cart-item__total">' + formatMoney(demo.price * demo.quantity) + '</span>' +
          '</div>';
        itemsContainer.appendChild(itemEl);
      });
    }

    // Calculate Combined Subtotal
    let subtotalCents = realCart ? realCart.total_price : 0;
    demoItems.forEach(function (demo) {
      subtotalCents += demo.price * demo.quantity;
    });

    if (subtotalEl) subtotalEl.textContent = formatMoney(subtotalCents);
    if (totalEl) totalEl.textContent = formatMoney(subtotalCents);
  }

  function refreshCartDrawer() {
    const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const sectionUrl = rootUrl + (rootUrl.endsWith('/') ? '' : '/') + '?section_id=custom-cart-drawer';

    return fetch(sectionUrl)
      .then(function (res) {
        if (!res.ok) throw new Error('Section rendering returned ' + res.status);
        return res.text();
      })
      .then(function (html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newDrawer = doc.getElementById('custom-cart-drawer');
        const currDrawer = document.getElementById('custom-cart-drawer');

        if (newDrawer && currDrawer) {
          const newBody = newDrawer.querySelector('[data-custom-cart-body]');
          const currBody = currDrawer.querySelector('[data-custom-cart-body]');
          if (newBody && currBody) currBody.innerHTML = newBody.innerHTML;

          const newFooter = newDrawer.querySelector('[data-custom-cart-footer]');
          const currFooter = currDrawer.querySelector('[data-custom-cart-footer]');
          if (newFooter && currFooter) {
            currFooter.innerHTML = newFooter.innerHTML;
            currFooter.style.display = newFooter.style.display;
          }
        }
        return fetch('/cart.js').then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        syncDemoCartWithDrawer(cart);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
        return cart;
      })
      .catch(function (err) {
        console.warn('Cart section render fallback to /cart.js:', err);
        return fetch('/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            syncDemoCartWithDrawer(cart);
            return cart;
          });
      });
  }

  function addToDemoAndSync(title, subtitle, price, image, addons, properties) {
    const demoCart = getDemoCart();
    const existing = demoCart.find(function (it) {
      return it.title === title && it.subtitle === subtitle && it.addons === addons;
    });

    if (existing) {
      existing.quantity += 1;
      if (properties) existing.properties = properties;
    } else {
      demoCart.push({
        title: title,
        subtitle: subtitle || '',
        price: price || 700,
        quantity: 1,
        image: image || '',
        addons: addons || '',
        properties: properties || {}
      });
    }
    setDemoCart(demoCart);

    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (realCart) {
        syncDemoCartWithDrawer(realCart);
      })
      .catch(function () {
        syncDemoCartWithDrawer(null);
      });

    showToast(title + ' added to bag');
    openCartDrawer();
  }

  function handleAddButtonClick(event) {
    const btn = event.target.closest('[data-custom-add-to-cart]');
    if (!btn) return;

    if (btn.disabled || btn.getAttribute('data-is-adding') === 'true') return;
    btn.setAttribute('data-is-adding', 'true');

    event.preventDefault();
    const variantId = btn.getAttribute('data-variant-id');
    const card = btn.closest('.custom-product-card');
    const productForm = btn.closest('#custom-product-form') || btn.closest('.custom-product-form');

    // Case 1: Product Page "Add to bag"
    if (productForm) {
      const sectionEl = btn.closest('.custom-product-page-section') || document.querySelector('.custom-product-page-section');
      const titleEl = sectionEl ? sectionEl.querySelector('.custom-product-title') : null;
      const title = titleEl ? titleEl.textContent.trim() : 'Fresh Drink';

      const sizeInput = productForm.querySelector('[data-prop-size]');
      const sizeVal = sizeInput ? sizeInput.value : '8oz';

      const milkInput = productForm.querySelector('[data-prop-milk]');
      const milkVal = milkInput ? milkInput.value : 'None';

      // Live scan selected Boosters and Inserts from the page (Globo or native)
      const selectedAddons = getLiveSelectedAddons(sectionEl);
      const boostersVal = selectedAddons.boostersStr;
      const insertsVal = selectedAddons.insertsStr;

      // Ensure hidden form inputs are also in sync
      const boostersInput = productForm.querySelector('[data-prop-boosters]');
      if (boostersInput) boostersInput.value = boostersVal;
      const insertsInput = productForm.querySelector('[data-prop-inserts]');
      if (insertsInput) insertsInput.value = insertsVal;

      const allAddons = [boostersVal, insertsVal].filter(Boolean).join(', ');
      const subtitle = sizeVal + (milkVal && milkVal !== 'None' ? ' · ' + milkVal + ' milk' : '');

      let priceCents = 700;
      if (sectionEl && sectionEl.getAttribute('data-current-total-cents')) {
        priceCents = parseInt(sectionEl.getAttribute('data-current-total-cents'), 10);
      } else if (sectionEl) {
        const livePriceEl = sectionEl.querySelector('[data-live-price]');
        if (livePriceEl) {
          const m = livePriceEl.textContent.match(/\$([0-9.]+)/);
          if (m) priceCents = Math.round(parseFloat(m[1]) * 100);
        }
      }

      const imgEl = sectionEl ? sectionEl.querySelector('[data-main-image]') : null;
      const img = imgEl ? imgEl.getAttribute('src') : '';

      const properties = {};
      if (milkVal && milkVal !== 'None') properties['Milk'] = milkVal;
      if (boostersVal) properties['Boosters'] = boostersVal;
      if (insertsVal) properties['Inserts'] = insertsVal;

      // Gather all custom properties from form, including Globo text inputs, textareas, radio selections, and standard properties
      if (sectionEl) {
        const allFormInputs = sectionEl.querySelectorAll(
          'input, select, textarea'
        );
        allFormInputs.forEach(function (inp) {
          if (!inp.value || inp.value.trim() === '') return;
          if ((inp.type === 'checkbox' || inp.type === 'radio') && !inp.checked) return;
          if (inp.type === 'hidden' && (inp.name === 'id' || inp.name === 'quantity' || inp.hasAttribute('data-selected-variant-id') || inp.hasAttribute('data-prop-size'))) return;

          let rawKey = '';
          const match = inp.name ? inp.name.match(/properties\[([^\]]+)\]/i) : null;
          if (match && match[1]) {
            rawKey = match[1].trim();
          } else if (inp.closest('.globo-form-control, .gpo-element, [class*="option-group"]')) {
            const groupEl = inp.closest('.globo-form-control, .gpo-element, [class*="option-group"]');
            const labelEl = groupEl ? groupEl.querySelector('.globo-label, .gpo-label, label, legend') : null;
            if (inp.getAttribute('data-title')) {
              rawKey = inp.getAttribute('data-title');
            } else if (inp.getAttribute('data-name')) {
              rawKey = inp.getAttribute('data-name');
            } else if (labelEl) {
              rawKey = labelEl.textContent.replace(/[\*:]/g, '').trim();
            } else if (inp.name) {
              rawKey = inp.name.replace(/^globo_option\[/, '').replace(/\]$/, '').trim();
            }
          }

          if (!rawKey) return;
          rawKey = rawKey.replace(/^properties\[/i, '').replace(/\]$/i, '').trim();
          const lowerKey = rawKey.toLowerCase();
          if (
            lowerKey === 'id' ||
            lowerKey === 'quantity' ||
            lowerKey === 'size' ||
            lowerKey === 'milk' ||
            lowerKey === 'boosters' ||
            lowerKey === 'inserts' ||
            lowerKey.startsWith('booster') ||
            lowerKey.startsWith('insert')
          ) {
            return;
          }

          const cleanVal = inp.value.trim();
          if (cleanVal && !properties[rawKey]) {
            properties[rawKey] = cleanVal;
          }
        });

        // Validation check for required fields in Globo or theme options
        let missingRequired = null;
        const requiredGroups = sectionEl.querySelectorAll('.globo-form-control:has(.required, .text-danger, [required]), [data-required="true"]');
        requiredGroups.forEach(function (grp) {
          if (missingRequired) return;
          const hasChecked = grp.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked');
          const textInp = grp.querySelector('input[type="text"], textarea');
          const hasText = textInp && textInp.value.trim().length > 0;
          if (!hasChecked && !hasText) {
            const lbl = grp.querySelector('label, .globo-label, legend');
            missingRequired = lbl ? lbl.textContent.replace(/[\*:]/g, '').trim() : 'Required Option';
            grp.scrollIntoView({ behavior: 'smooth', block: 'center' });
            let errorMsg = grp.querySelector('.text-danger, .globo-form-control-error');
            if (!errorMsg) {
              errorMsg = document.createElement('div');
              errorMsg.className = 'globo-form-control-error text-danger';
              grp.appendChild(errorMsg);
            }
            errorMsg.textContent = 'Please select ' + missingRequired;
            errorMsg.style.display = 'block';
          }
        });

        if (missingRequired) {
          btn.removeAttribute('data-is-adding');
          showToast('Please select ' + missingRequired);
          return;
        }
      }

      const effectiveVariantId = (productForm.querySelector('[data-selected-variant-id]') || {}).value || variantId;
      const isLiveVariant = effectiveVariantId && effectiveVariantId !== '1' && /^\d{5,}$/.test(effectiveVariantId);

      if (isLiveVariant) {
        btn.disabled = true;
        const originalText = btn.innerHTML;
        btn.textContent = '...';

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: effectiveVariantId,
              quantity: 1,
              properties: properties
            }]
          })
        })
          .then(function (res) {
            if (!res.ok) return res.json().then(function (err) { throw err; });
            return res.json();
          })
          .then(function (data) {
            showToast(title + ' added to bag');
            return refreshCartDrawer();
          })
          .then(function () {
            openCartDrawer();
          })
          .catch(function (err) {
            console.warn('Real /cart/add.js fallback to demo:', err);
            addToDemoAndSync(title, subtitle, priceCents, img, allAddons, properties);
          })
          .finally(function () {
            btn.disabled = false;
            btn.removeAttribute('data-is-adding');
            btn.innerHTML = originalText;
          });
        return;
      } else {
        addToDemoAndSync(title, subtitle, priceCents, img, allAddons, properties);
        setTimeout(function () {
          btn.removeAttribute('data-is-adding');
        }, 400);
        return;
      }
    }

    // Case 2: Card / Quick Add
    if (!variantId || variantId === '1') {
      let title = 'Fresh Drink';
      let subtitle = '';
      let price = 700;
      let image = '';

      if (card) {
        const titleEl = card.querySelector('.custom-product-card__title a, .custom-product-card__title');
        if (titleEl) title = titleEl.textContent.trim();

        const ingEl = card.querySelector('.custom-product-card__ing');
        if (ingEl) subtitle = ingEl.textContent.trim();

        const priceEl = card.querySelector('.custom-product-card__price');
        if (priceEl) {
          const matched = priceEl.textContent.match(/\$([0-9.]+)/);
          if (matched) price = Math.round(parseFloat(matched[1]) * 100);
        }

        const imgEl = card.querySelector('.custom-product-card__img');
        if (imgEl) image = imgEl.getAttribute('src');
      }

      addToDemoAndSync(title, subtitle, price, image, '');
      setTimeout(function () {
        btn.removeAttribute('data-is-adding');
      }, 400);
      return;
    }

    // Live Shopify variant add to cart from card
    btn.disabled = true;
    const originalText = btn.textContent;
    btn.textContent = '...';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1
      })
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (err) { throw err; });
        }
        return res.json();
      })
      .then(function (data) {
        showToast((data.title || 'Item') + ' added to bag');
        return refreshCartDrawer();
      })
      .then(function () {
        openCartDrawer();
      })
      .catch(function (err) {
        console.error('Error adding to cart:', err);
        showToast(err.description || 'Could not add item to bag');
      })
      .finally(function () {
        btn.disabled = false;
        btn.removeAttribute('data-is-adding');
        btn.textContent = originalText;
      });
  }


  function handleCartDrawerClicks(event) {
    // Open trigger
    const openTrigger = event.target.closest('[data-custom-cart-open]');
    if (openTrigger) {
      event.preventDefault();
      openCartDrawer();
      return;
    }

    // Close trigger or overlay
    const closeTrigger = event.target.closest('[data-custom-cart-close]');
    if (closeTrigger) {
      event.preventDefault();
      closeCartDrawer();
      return;
    }

    // Drawer line actions: Increase / Decrease / Remove
    const removeBtn = event.target.closest('.custom-cart-item__remove');
    const qtyBtn = event.target.closest('.custom-cart-qty-btn');

    if (removeBtn) {
      event.preventDefault();
      const action = removeBtn.getAttribute('data-action');

      if (action === 'demo-remove') {
        const idx = parseInt(removeBtn.getAttribute('data-demo-index'), 10);
        const demoCart = getDemoCart();
        demoCart.splice(idx, 1);
        setDemoCart(demoCart);
        fetch('/cart.js').then(function (r) { return r.json(); }).then(syncDemoCartWithDrawer);
        return;
      }

      const lineKey = removeBtn.getAttribute('data-line-key');
      const itemEl = removeBtn.closest('.custom-cart-item');
      if (itemEl) itemEl.classList.add('custom-cart-item--updating');

      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 0 })
      })
        .then(function () { return refreshCartDrawer(); })
        .catch(function (e) { console.error('Error removing item:', e); });
      return;
    }

    if (qtyBtn) {
      event.preventDefault();
      const action = qtyBtn.getAttribute('data-action');

      if (action === 'demo-increase' || action === 'demo-decrease') {
        const idx = parseInt(qtyBtn.getAttribute('data-demo-index'), 10);
        const demoCart = getDemoCart();
        if (demoCart[idx]) {
          if (action === 'demo-increase') {
            demoCart[idx].quantity += 1;
          } else {
            demoCart[idx].quantity -= 1;
            if (demoCart[idx].quantity <= 0) demoCart.splice(idx, 1);
          }
          setDemoCart(demoCart);
          fetch('/cart.js').then(function (r) { return r.json(); }).then(syncDemoCartWithDrawer);
        }
        return;
      }

      const lineKey = qtyBtn.getAttribute('data-line-key');
      const currentQty = parseInt(qtyBtn.getAttribute('data-current-qty'), 10) || 1;
      const newQty = action === 'increase' ? currentQty + 1 : Math.max(0, currentQty - 1);

      const itemEl = qtyBtn.closest('.custom-cart-item');
      if (itemEl) itemEl.classList.add('custom-cart-item--updating');

      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: newQty })
      })
        .then(function () { return refreshCartDrawer(); })
        .catch(function (e) { console.error('Error changing quantity:', e); });
      return;
    }
  }

  // Intercept standard product add-to-cart forms
  function handleFormSubmit(event) {
    const form = event.target.closest('form[action*="/cart/add"]');
    if (!form) return;

    // Check if form should be AJAX intercepted
    event.preventDefault();
    const submitBtn = form.querySelector('[type="submit"], [name="add"]');
    if (submitBtn) submitBtn.disabled = true;

    const section = form.closest('.custom-product-page-section') || document.querySelector('.custom-product-page-section');
    if (section) updateLiveProductPrice(section);

    fetch('/cart/add.js', {
      method: 'POST',
      body: new FormData(form),
      headers: { 'Accept': 'application/json' }
    })
      .then(function (res) {
        if (!res.ok) return res.json().then(function (e) { throw e; });
        return res.json();
      })
      .then(function (data) {
        showToast((data.title || 'Item') + ' added to bag');
        return refreshCartDrawer();
      })
      .then(function () {
        openCartDrawer();
      })
      .catch(function (err) {
        console.error('Error submitting form to cart:', err);
        // If fetch fails, submit normally
        form.submit();
      })
      .finally(function () {
        if (submitBtn) submitBtn.disabled = false;
      });
  }

  function handleMenuJumpClicks(event) {
    const link = event.target.closest('[data-menu-jump]');
    if (!link) return;

    const href = link.getAttribute('href');
    if (!href || !href.startsWith('#')) return;

    const targetId = href.substring(1);
    const targetEl = document.getElementById(targetId);
    if (!targetEl) return;

    event.preventDefault();
    const headerOffset = 135;
    const elementPosition = targetEl.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });

    const allLinks = document.querySelectorAll('[data-menu-jump]');
    allLinks.forEach(function (l) { l.classList.remove('custom-menu-subnav__link--active'); });
    link.classList.add('custom-menu-subnav__link--active');
  }

  // Product Page Interactivity
  function handleAccordionClick(event) {
    const toggle = event.target.closest('[data-accordion-toggle]');
    if (!toggle) return;

    event.preventDefault();
    const item = toggle.closest('.custom-product-accordion-item');
    if (!item) return;

    const body = item.querySelector('[data-accordion-body]');
    const icon = item.querySelector('[data-accordion-icon]');
    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';

    if (isExpanded) {
      toggle.setAttribute('aria-expanded', 'false');
      if (body) body.style.display = 'none';
      if (icon) icon.textContent = '+';
    } else {
      toggle.setAttribute('aria-expanded', 'true');
      if (body) body.style.display = 'block';
      if (icon) icon.textContent = '–';
    }
  }

  function handleGalleryThumbClick(event) {
    const thumb = event.target.closest('[data-thumb-index]');
    if (!thumb) return;

    event.preventDefault();
    const gallery = thumb.closest('[data-product-gallery]');
    if (!gallery) return;

    const allThumbs = gallery.querySelectorAll('[data-thumb-index]');
    allThumbs.forEach(function (t) {
      t.classList.remove('custom-product-gallery__thumb-btn--active');
    });
    thumb.classList.add('custom-product-gallery__thumb-btn--active');

    const thumbSrc = thumb.getAttribute('data-thumb-src');
    const mainImg = gallery.querySelector('[data-main-image]');
    if (thumbSrc && mainImg) {
      mainImg.src = thumbSrc;
    }
  }

  /* ==========================================================================
     Boosters & Inserts Option Extractor & Live Price Calculator
     ========================================================================== */
  function cleanAddonName(text) {
    if (!text) return '';
    return text
      .replace(/[\n\r\t]+/g, ' ')
      .replace(/\s*[\(\[–—-]?\s*\+?\$[0-9.]+\s*[\)\]]?/gi, '')
      .replace(/[✓✔\u2713\u2714]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Accurately classify whether an option element or pill belongs to 'booster' or 'insert'.
   * Multi-tiered classification:
   * 1. Direct theme data attributes on element or wrapper
   * 2. Input attributes (name, id, data-*)
   * 3. Nearest container heading in bottom-up ancestor tree
   * 4. Preceding standalone group heading in document order
   * 5. Exclusive items knowledge fallback
   */
  function classifyAddonItem(el, rawText, sectionEl) {
    if (!el) return null;
    const cleanText = cleanAddonName(rawText || '');

    // 1. Direct Theme Data Attributes on element or closest wrapper
    const dataBooster = el.closest('[data-addon-type="booster"], [data-booster-options]');
    if (dataBooster) return 'booster';
    const dataInsert = el.closest('[data-addon-type="insert"], [data-insert-options]');
    if (dataInsert) return 'insert';

    // 2. Input Attributes (name, id, data-name, data-label)
    const inp = el.matches('input') ? el : el.querySelector('input') || (el.getAttribute('for') ? sectionEl.querySelector('#' + el.getAttribute('for')) : null);
    if (inp) {
      const nameAttr = (inp.name || '').toLowerCase();
      const idAttr = (inp.id || '').toLowerCase();
      const dataAttr = (inp.getAttribute('data-name') || inp.getAttribute('data-label') || '').toLowerCase();

      if (nameAttr.includes('booster') || idAttr.includes('booster') || dataAttr.includes('booster')) {
        return 'booster';
      }
      if (nameAttr.includes('insert') || idAttr.includes('insert') || dataAttr.includes('insert')) {
        return 'insert';
      }
    }

    // 3. Bottom-up Ancestor Heading Search (Find nearest group container)
    let curr = el.parentElement;
    while (curr && curr !== sectionEl && curr !== document.body) {
      const headings = Array.from(curr.querySelectorAll(
        ':scope > label, :scope > legend, :scope > .globo-label, :scope > .gpo-label, :scope > [class*="label"], :scope > [class*="title"], :scope > [class*="heading"], :scope > h1, :scope > h2, :scope > h3, :scope > h4, :scope > h5'
      )).filter(function (h) {
        return !h.querySelector('input') && !h.closest('.checkbox') && !h.closest('[class*="choice"]');
      });

      let hasBooster = false;
      let hasInsert = false;
      headings.forEach(function (h) {
        const t = h.textContent.trim().toUpperCase();
        if (t.includes('BOOSTER')) hasBooster = true;
        if (t.includes('INSERT')) hasInsert = true;
      });

      // If this container is specific to ONE group, return that group!
      if (hasBooster && !hasInsert) return 'booster';
      if (hasInsert && !hasBooster) return 'insert';

      // Check immediate previous sibling of curr
      const prev = curr.previousElementSibling;
      if (prev) {
        const pText = prev.textContent.trim().toUpperCase();
        if (pText.includes('BOOSTER') && !pText.includes('INSERT')) return 'booster';
        if (pText.includes('INSERT') && !pText.includes('BOOSTER')) return 'insert';
      }

      curr = curr.parentElement;
    }

    // 4. Document Position relative to group headings (BOOSTERS vs INSERTS)
    const groupHeadings = Array.from(sectionEl.querySelectorAll(
      'label.globo-label, .gpo-label, legend, .custom-product-info label, .custom-product-info [class*="title"], .custom-product-info [class*="label"], .custom-product-info [class*="heading"], h2, h3, h4, h5'
    )).filter(function (h) {
      if (h.querySelector('input') || h.closest('.checkbox') || h.closest('[class*="choice"]')) return false;
      const t = h.textContent.trim().toUpperCase();
      const isB = t.includes('BOOSTER');
      const isI = t.includes('INSERT');
      return (isB || isI) && !(isB && isI);
    });

    let nearestPrecedingHeading = null;
    groupHeadings.forEach(function (h) {
      if (h.contains(el)) return;
      const pos = h.compareDocumentPosition(el);
      if (pos & Node.DOCUMENT_POSITION_FOLLOWING) {
        nearestPrecedingHeading = h;
      }
    });

    if (nearestPrecedingHeading) {
      const hText = nearestPrecedingHeading.textContent.trim().toUpperCase();
      if (hText.includes('BOOSTER')) return 'booster';
      if (hText.includes('INSERT')) return 'insert';
    }

    // 5. Exclusive Items Knowledge Base (items that only exist in Boosters or only in Inserts)
    const EXCLUSIVE_BOOSTERS = [
      'Ashwagandha', 'Camu Camu', 'Cinnamon', 'Cordyceps', 'Echinacea',
      'Flax Seeds', 'Hemp Seeds', "Lion's Mane", 'Lions Mane', 'Matcha',
      'Maca', 'Pea Protein', 'Raw Cacao', 'Reishi', 'Sea Moss', 'Spirulina', 'Turmeric'
    ];
    const EXCLUSIVE_INSERTS = [
      'Pumpkin Seeds', 'Cashew', 'Walnut', 'Almond', 'Coconut Chips', 'Bee Pollen', 'Dates'
    ];

    if (cleanText) {
      const isExBooster = EXCLUSIVE_BOOSTERS.some(function (b) { return b.toLowerCase() === cleanText.toLowerCase(); });
      if (isExBooster) return 'booster';

      const isExInsert = EXCLUSIVE_INSERTS.some(function (i) { return i.toLowerCase() === cleanText.toLowerCase(); });
      if (isExInsert) return 'insert';
    }

    return null;
  }

  function getLiveSelectedAddons(sectionEl) {
    sectionEl = sectionEl || document.querySelector('.custom-product-page-section') || document;

    const boosters = [];
    const inserts = [];

    function addBooster(name) {
      const clean = cleanAddonName(name);
      if (clean && !boosters.includes(clean)) boosters.push(clean);
    }

    function addInsert(name) {
      const clean = cleanAddonName(name);
      if (clean && !inserts.includes(clean)) inserts.push(clean);
    }

    // Process checked inputs and active UI pills
    const seenElements = new Set();

    // 1. Process all checked checkboxes / radios
    const checkedBoxes = sectionEl.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked');
    checkedBoxes.forEach(function (cb) {
      if (cb.name === 'id' || cb.name === 'quantity' || cb.hasAttribute('data-prop-size') || cb.hasAttribute('data-prop-milk')) return;
      seenElements.add(cb);

      const label = cb.closest('label') || (cb.id ? sectionEl.querySelector('label[for="' + cb.id + '"]') : null) || cb.parentElement;
      const rawText = label ? label.textContent : cb.value;
      const clean = cleanAddonName(rawText || cb.value || '');
      if (!clean) return;

      const groupType = classifyAddonItem(cb, clean, sectionEl);
      if (groupType === 'booster') addBooster(clean);
      else if (groupType === 'insert') addInsert(clean);
    });

    // 2. Process active/selected pills in the UI
    const activePills = sectionEl.querySelectorAll(
      '.custom-chip-pill--active, .checkbox.active, .checkbox.selected, .checkbox.checked, [class*="choice"].active, [class*="choice"].selected, label.active, label.selected'
    );
    activePills.forEach(function (pill) {
      if (pill.hasAttribute('data-size-chip') || pill.hasAttribute('data-milk-chip') || pill.closest('[data-size-options], [data-milk-options]')) return;

      const innerCb = pill.querySelector('input');
      if (innerCb && seenElements.has(innerCb)) return;
      if (seenElements.has(pill)) return;
      seenElements.add(pill);

      const rawText = pill.getAttribute('data-addon-value') || pill.textContent;
      const clean = cleanAddonName(rawText || '');
      if (!clean) return;

      const groupType = classifyAddonItem(pill, clean, sectionEl);
      if (groupType === 'booster') addBooster(clean);
      else if (groupType === 'insert') addInsert(clean);
    });

    return {
      boosters: boosters,
      inserts: inserts,
      boostersCount: boosters.length,
      insertsCount: inserts.length,
      boostersStr: boosters.join(', '),
      insertsStr: inserts.join(', ')
    };
  }

  function updateLiveProductPrice(sectionEl) {
    if (!sectionEl) return;
    const basePriceCents = parseInt(sectionEl.getAttribute('data-base-price'), 10) || 700;

    const selectedAddons = getLiveSelectedAddons(sectionEl);

    const totalCents = basePriceCents + (selectedAddons.boostersCount * 150) + (selectedAddons.insertsCount * 100);
    const formattedPrice = '$' + (totalCents / 100).toFixed(2);

    const priceEls = sectionEl.querySelectorAll('[data-live-price]');
    priceEls.forEach(function (el) {
      el.textContent = formattedPrice;
    });

    const buttonPriceEls = sectionEl.querySelectorAll('[data-button-price]');
    buttonPriceEls.forEach(function (el) {
      el.textContent = formattedPrice;
    });

    sectionEl.setAttribute('data-current-total-cents', totalCents);

    // Sync to hidden form inputs so they are always in sync
    const bInput = sectionEl.querySelector('[data-prop-boosters]');
    if (bInput) bInput.value = selectedAddons.boostersStr;

    const iInput = sectionEl.querySelector('[data-prop-inserts]');
    if (iInput) iInput.value = selectedAddons.insertsStr;
  }

  /* ==========================================================================
     Format Addon Group Titles (Boosters, Inserts)
     ========================================================================== */
  function formatAddonGroupTitles() {
    const headings = document.querySelectorAll(
      '.custom-product-info label, .custom-product-info legend, .custom-product-info [class*="label"], .custom-product-info [class*="title"], .globo-label, .gpo-label'
    );
    headings.forEach(function (el) {
      if (el.querySelector('input') || el.closest('.checkbox') || el.closest('[class*="choice"]')) return;
      const t = el.textContent.trim().toUpperCase();
      if (t.startsWith('BOOSTER')) {
        el.textContent = 'BOOSTERS — $1.50 EACH';
      } else if (t.startsWith('INSERT')) {
        el.textContent = 'INSERTS — $1.00 EACH';
      }
    });
  }

  /* ==========================================================================
     Clean up Globo Product Options Widget:
     1. Remove checkbox indicator square boxes inside pills.
     2. Suppress selected-value preview texts under BOOSTERS/INSERTS headings
        to prevent layout jump.
     ========================================================================== */
  function injectGloboFixStyle() {
    if (document.getElementById('custom-globo-fix-style')) return;
    const style = document.createElement('style');
    style.id = 'custom-globo-fix-style';
    style.textContent = `
      html body .globo-form-app .checkbox label::before,
      html body .globo-form-app .checkbox label::after,
      html body .globo-form-app .radio label::before,
      html body .globo-form-app .radio label::after,
      html body .globo-form-control .checkbox label::before,
      html body .globo-form-control .checkbox label::after,
      html body .globo-form-control .radio label::before,
      html body .globo-form-control .radio label::after,
      html body .globo-form-control label::before,
      html body .globo-form-control label::after,
      html body .custom-product-info .checkbox label::before,
      html body .custom-product-info .checkbox label::after,
      html body .custom-product-info .radio label::before,
      html body .custom-product-info .radio label::after,
      html body [id*="globo"] label::before,
      html body [id*="globo"] label::after {
        content: none !important;
        display: none !important;
        width: 0 !important;
        height: 0 !important;
        border: none !important;
      }
      html body .custom-product-info .checkbox,
      html body .custom-product-info .radio,
      html body .globo-form-control .checkbox,
      html body .globo-form-control .radio {
        display: inline-flex !important;
        visibility: visible !important;
        opacity: 1 !important;
        width: auto !important;
      }
      html body .custom-product-info .globo-selected-value,
      html body .globo-form-control .globo-selected-value,
      html body .custom-product-info [class*="selected-val"],
      html body .globo-form-control [class*="selected-val"] {
        display: none !important;
        height: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
        overflow: hidden !important;
      }
    `;
    document.head.appendChild(style);
  }

  function cleanUpGloboWidget() {
    injectGloboFixStyle();
    const container = document.querySelector('.custom-product-info') || document;

    // 1. Ensure all pills (checkboxes AND radios) are visible and active
    const pills = container.querySelectorAll(
      '.globo-form-control .checkbox, .globo-form-control .radio, .custom-product-info .checkbox, .custom-product-info .radio, .globo-form-control [class*="choice"], .custom-product-info [class*="choice"]'
    );
    pills.forEach(function (pill) {
      pill.style.setProperty('display', 'inline-flex', 'important');
      pill.style.setProperty('visibility', 'visible', 'important');
      pill.style.setProperty('opacity', '1', 'important');
      pill.style.setProperty('width', 'auto', 'important');

      // Check if internal input is checked, keep active classes in sync
      const innerInput = pill.querySelector('input[type="radio"], input[type="checkbox"]');
      if (innerInput && innerInput.checked) {
        pill.classList.add('checked', 'selected', 'active', 'custom-chip-pill--active');
        const pillLabel = pill.querySelector('label');
        if (pillLabel) pillLabel.classList.add('checked', 'selected', 'active', 'custom-chip-pill--active');
      }

      // Inside each pill label, if there is an empty span or icon/svg, hide it
      const label = pill.querySelector('label') || pill;
      Array.from(label.children).forEach(function (child) {
        if (child.tagName === 'INPUT') {
          child.style.setProperty('position', 'absolute', 'important');
          child.style.setProperty('opacity', '0', 'important');
          child.style.setProperty('width', '1px', 'important');
          child.style.setProperty('height', '1px', 'important');
          child.style.setProperty('clip', 'rect(0, 0, 0, 0)', 'important');
          child.style.setProperty('pointer-events', 'none', 'important');
          return;
        }
        const tag = child.tagName.toLowerCase();
        const txt = child.textContent.trim();
        // Hide only if it's an svg, an icon tag <i>, or it has no text (empty box/dot)
        if (tag === 'svg' || tag === 'i' || txt === '' || txt === '✓' || txt === '✔') {
          child.style.setProperty('display', 'none', 'important');
          child.style.setProperty('width', '0px', 'important');
          child.style.setProperty('height', '0px', 'important');
          child.style.setProperty('border', 'none', 'important');
        }
      });
    });

    // 2. Hide only selected-value preview text elements (e.g. Camu Camu under heading)
    const previews = container.querySelectorAll(
      '.globo-selected-value, .globo-selected-values, [class*="selected-val"], [class*="selected_val"], [class*="selected-text"], [class*="selected_text"]'
    );
    previews.forEach(function (p) {
      // NEVER hide a pill, label, input, textarea, select or description!
      if (!p.matches('.checkbox, .checkbox *, .radio, .radio *, label, label *, input, textarea, select, [class*="choice"], [class*="choice"] *, .globo-description, [class*="description"], .text-danger, .help-block')) {
        p.style.setProperty('display', 'none', 'important');
        p.style.setProperty('height', '0px', 'important');
        p.style.setProperty('margin', '0px', 'important');
        p.style.setProperty('padding', '0px', 'important');
        p.style.setProperty('overflow', 'hidden', 'important');
      }
    });

    // 3. Keep group titles cleanly formatted
    formatAddonGroupTitles();
  }

  function handleRadioOptionClick(event) {
    const radioContainer = event.target.closest('.radio, .globo-form-control .radio, .custom-product-info .radio');
    const radioLabel = event.target.closest('label:has(input[type="radio"]), .radio label');
    const radioInput = event.target.matches('input[type="radio"]') ? event.target : null;

    const targetEl = radioInput || radioLabel || radioContainer;
    if (!targetEl) return;

    const wrap = targetEl.closest('.globo-form-control-content, [class*="choices"], .custom-product-info, form') || document;
    const inp = targetEl.matches('input[type="radio"]') ? targetEl : targetEl.querySelector('input[type="radio"]');

    if (inp) {
      inp.checked = true;
      inp.dispatchEvent(new Event('change', { bubbles: true }));
      inp.dispatchEvent(new Event('input', { bubbles: true }));

      // Sync active classes across all sibling radios in the same named group
      if (inp.name) {
        const siblingRadios = wrap.querySelectorAll('input[type="radio"][name="' + CSS.escape(inp.name) + '"]');
        siblingRadios.forEach(function (sib) {
          const sibWrap = sib.closest('.radio, [class*="choice"]') || sib.closest('label') || sib.parentElement;
          const sibLabel = sib.closest('label') || (sibWrap ? sibWrap.querySelector('label') : null);
          if (sibWrap) {
            sibWrap.classList.remove('active', 'selected', 'checked', 'custom-chip-pill--active');
          }
          if (sibLabel) {
            sibLabel.classList.remove('active', 'selected', 'checked', 'custom-chip-pill--active');
          }
        });
      }
    }

    const currentWrap = targetEl.closest('.radio, [class*="choice"]') || targetEl.closest('label');
    const currentLabel = targetEl.closest('label') || (currentWrap ? currentWrap.querySelector('label') : null);
    if (currentWrap) {
      currentWrap.classList.add('active', 'selected', 'checked', 'custom-chip-pill--active');
    }
    if (currentLabel) {
      currentLabel.classList.add('active', 'selected', 'checked', 'custom-chip-pill--active');
    }

    // Clear error message if user selected a required option
    const groupEl = targetEl.closest('.globo-form-control');
    if (groupEl) {
      const err = groupEl.querySelector('.globo-form-control-error, .text-danger');
      if (err) err.style.display = 'none';
    }

    const sectionEl = targetEl.closest('.custom-product-page-section') || document.querySelector('.custom-product-page-section');
    if (sectionEl) {
      updateLiveProductPrice(sectionEl);
    }
  }

  function handleSizeOptionClick(event) {
    const chip = event.target.closest('[data-size-chip]');
    if (!chip) return;

    event.preventDefault();
    const container = chip.closest('[data-size-options]');
    const sectionEl = chip.closest('.custom-product-page-section') || chip.closest('[data-section-id]');
    if (!container || !sectionEl) return;

    const allChips = container.querySelectorAll('[data-size-chip]');
    allChips.forEach(function (c) {
      c.classList.remove('custom-chip-pill--active');
    });
    chip.classList.add('custom-chip-pill--active');

    const variantId = chip.getAttribute('data-variant-id');
    const variantTitle = chip.getAttribute('data-variant-title');
    const variantPrice = parseInt(chip.getAttribute('data-variant-price'), 10) || 700;

    const variantIdInput = sectionEl.querySelector('[data-selected-variant-id]');
    if (variantIdInput && variantId) variantIdInput.value = variantId;

    const addBtn = sectionEl.querySelector('[data-custom-add-to-cart]');
    if (addBtn && variantId) addBtn.setAttribute('data-variant-id', variantId);

    const sizeInput = sectionEl.querySelector('[data-prop-size]');
    if (sizeInput && variantTitle) sizeInput.value = variantTitle;

    sectionEl.setAttribute('data-base-price', variantPrice);
    updateLiveProductPrice(sectionEl);
  }

  function handleMilkOptionClick(event) {
    const chip = event.target.closest('[data-milk-chip]');
    if (!chip) return;

    event.preventDefault();
    const container = chip.closest('[data-milk-options]');
    const sectionEl = chip.closest('.custom-product-page-section') || chip.closest('[data-section-id]');
    if (!container || !sectionEl) return;

    const allChips = container.querySelectorAll('[data-milk-chip]');
    allChips.forEach(function (c) {
      c.classList.remove('custom-chip-pill--active');
    });
    chip.classList.add('custom-chip-pill--active');

    const milkVal = chip.getAttribute('data-milk-value') || 'None';
    const milkInput = sectionEl.querySelector('[data-prop-milk]');
    if (milkInput) milkInput.value = milkVal;
  }

  function handleAddonOptionClick(event) {
    const chip = event.target.closest('[data-addon-chip]');
    if (!chip) return;

    event.preventDefault();
    const sectionEl = chip.closest('.custom-product-page-section') || chip.closest('[data-section-id]');
    if (!sectionEl) return;

    chip.classList.toggle('custom-chip-pill--active');

    const boosterChips = sectionEl.querySelectorAll('[data-addon-type="booster"].custom-chip-pill--active');
    const boostersList = Array.from(boosterChips).map(function (c) { return c.getAttribute('data-addon-value'); });
    const boosterInput = sectionEl.querySelector('[data-prop-boosters]');
    if (boosterInput) boosterInput.value = boostersList.join(', ');

    const insertChips = sectionEl.querySelectorAll('[data-addon-type="insert"].custom-chip-pill--active');
    const insertsList = Array.from(insertChips).map(function (c) { return c.getAttribute('data-addon-value'); });
    const insertInput = sectionEl.querySelector('[data-prop-inserts]');
    if (insertInput) insertInput.value = insertsList.join(', ');

    updateLiveProductPrice(sectionEl);
  }

  // Expose global drawer controls for other components or scripts
  window.CustomCartDrawer = {
    open: openCartDrawer,
    close: closeCartDrawer,
    refresh: refreshCartDrawer
  };

  document.addEventListener('DOMContentLoaded', function () {
    document.addEventListener('click', function (e) {
      handleMobileMenuClicks(e);
      handleAddButtonClick(e);
      handleCartDrawerClicks(e);
      handleMenuJumpClicks(e);
      handleAccordionClick(e);
      handleGalleryThumbClick(e);
      handleSizeOptionClick(e);
      handleMilkOptionClick(e);
      handleAddonOptionClick(e);
      handleRadioOptionClick(e);

      // Clean up Globo widget & update live price whenever user clicks anywhere on product options
      const optTarget = e.target.closest('.custom-product-info, .globo-form-control, .checkbox, .radio, label, [class*="choice"]');
      if (optTarget) {
        const section = optTarget.closest('.custom-product-page-section') || document.querySelector('.custom-product-page-section');
        setTimeout(function () {
          cleanUpGloboWidget();
          if (section) updateLiveProductPrice(section);
        }, 15);
        setTimeout(function () {
          cleanUpGloboWidget();
          if (section) updateLiveProductPrice(section);
        }, 80);
      }
    });

    document.addEventListener('submit', handleFormSubmit);

    // Close on Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        closeCartDrawer();
        closeMobileMenu();
      }
    });

    // Listen for checkbox & input changes on product page to update live price & clean up previews
    document.addEventListener('change', function (e) {
      const section = e.target.closest('.custom-product-page-section') || document.querySelector('.custom-product-page-section');
      if (section) {
        setTimeout(function () {
          updateLiveProductPrice(section);
          cleanUpGloboWidget();
        }, 15);
      }
    });

    // Clean up Globo option widget at safe intervals as app finishes loading
    cleanUpGloboWidget();
    setTimeout(cleanUpGloboWidget, 150);
    setTimeout(cleanUpGloboWidget, 400);
    setTimeout(cleanUpGloboWidget, 1000);
    setTimeout(cleanUpGloboWidget, 2500);

    // Initial sync with live cart and any saved demo items
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        syncDemoCartWithDrawer(cart);
      })
      .catch(function () {
        syncDemoCartWithDrawer(null);
      });
  });
})();

